import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LuUserPlus } from 'react-icons/lu';
import AuthContext from '../../components/shared/AuthContext';
import { useTeamContext } from '../../context/TeamContext';
import TeamMemberRoleSelect from './TeamMemberRoleSelect';
import { useOrgDetail } from './useOrgDetail';

export default function TeamMembersPage() {
  const { orgId } = useParams();
  const { apiClient, user } = useContext(AuthContext);
  const { activeOrg, refresh: refreshTeamContext } = useTeamContext();
  const { canManageMemberRoles } = useOrgDetail(apiClient, orgId, user);
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [pendingInvites, setPendingInvites] = useState([]);
  const [roleSavingId, setRoleSavingId] = useState(null);
  const [error, setError] = useState('');

  const canInvite = useMemo(() => {
    const role = (activeOrg?.role || '').toUpperCase();
    return activeOrg?.isOwner || role === 'OWNER' || role === 'ADMIN' || role === 'MANAGER';
  }, [activeOrg]);

  const load = async () => {
    const [membersRes, invitesRes] = await Promise.all([
      apiClient.get(`/api/organizations/${orgId}/members`),
      canInvite
        ? apiClient.get(`/api/organizations/${orgId}/invitations?status=PENDING`).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
    ]);
    setMembers(membersRes.data || []);
    setPendingInvites(Array.isArray(invitesRes.data) ? invitesRes.data : []);
  };

  useEffect(() => {
    load().catch(() => setError('Could not load members.'));
    refreshTeamContext().catch(() => {});
  }, [apiClient, orgId, refreshTeamContext, canInvite]);

  const changeRole = async (memberUserId, newRole) => {
    setError('');
    setRoleSavingId(memberUserId);
    try {
      await apiClient.patch(`/api/organizations/${orgId}/members/${memberUserId}`, { role: newRole });
      await load();
      await refreshTeamContext();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'Could not update role.');
    } finally {
      setRoleSavingId(null);
    }
  };

  const copyLink = (acceptPath) => {
    const link = `${window.location.origin}${acceptPath}`;
    setInviteLink(link);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).catch(() => {});
    }
  };

  const revokeInvite = async (invitationId) => {
    setError('');
    try {
      await apiClient.delete(`/api/organizations/${orgId}/invitations/${invitationId}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'Could not revoke invite.');
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    setError('');
    setInviteLink('');
    try {
      const res = await apiClient.post(`/api/organizations/${orgId}/invitations`, { email, role: 'MEMBER' });
      setInviteLink(`${window.location.origin}${res.data.acceptPath}`);
      setEmail('');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.response?.data?.message || 'Invite failed.');
    }
  };

  return (
    <>
      <div className="teams-card">
        <h2><LuUserPlus aria-hidden /> Invite member</h2>
        {error && <div className="teams-error">{error}</div>}
        {canInvite ? (
          <>
            <form onSubmit={invite} className="teams-form-row" style={{ flexDirection: 'row', alignItems: 'end', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="invite-email">Email</label>
                <input id="invite-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <button type="submit" className="teams-btn">Send invite</button>
            </form>
            {inviteLink && (
              <p className="teams-sub">Share accept link: <code>{inviteLink}</code></p>
            )}
            <p className="teams-sub">
              Invited users also see this under Study hub notifications or Teams → Requests once they log in with the same email.
            </p>
            {pendingInvites.length > 0 && (
              <div className="teams-pending-invites">
                <h3 className="teams-assignment-subhead">Pending email invites</h3>
                <ul className="teams-approval-list">
                  {pendingInvites.map((inv) => (
                    <li key={inv.id} className="teams-approval-item">
                      <div>
                        <strong>{inv.email}</strong>
                        <p className="teams-sub">{inv.summary}</p>
                      </div>
                      <div className="teams-request-actions">
                        <button
                          type="button"
                          className="teams-btn teams-btn--secondary"
                          onClick={() => copyLink(inv.acceptPath)}
                        >
                          Copy link
                        </button>
                        <button
                          type="button"
                          className="teams-btn teams-btn--danger"
                          onClick={() => revokeInvite(inv.invitationId ?? inv.id)}
                        >
                          Revoke
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <p className="teams-sub">
            Only the workspace owner, admin, or manager can send email invites. Ask them to add you or upgrade your role.
          </p>
        )}
      </div>

      <div className="teams-table-wrap">
        <div className="teams-members-head">
          <h2 className="teams-members-title">Members</h2>
          {canManageMemberRoles && (
            <p className="teams-sub">
              Change roles below or in <Link to={`/teams/${orgId}/settings`}>Settings → Member roles</Link>.
            </p>
          )}
        </div>
        <table className="teams-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.userId}>
                <td>{m.name || '—'}</td>
                <td>{m.email}</td>
                <td>
                  {canManageMemberRoles ? (
                    <TeamMemberRoleSelect
                      member={m}
                      disabled={roleSavingId === m.userId}
                      onChange={changeRole}
                    />
                  ) : (
                    m.role
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
